import {Injectable, inject} from "@angular/core";
import * as SustainabilityRatingGraphqlModels from "@app/graphql-typings/graphql-sustainability-ratings";
import {Observable, map} from "rxjs";
import {BaseService} from "../base-service";
import {APIPageInfo, GQLPagingParamsVariable, GRAPHQL_TYPES, HttpUtilityService, PagingParams} from "../http-utility-service";
import {ClassTransformer} from "class-transformer";
import * as gql from 'gql-query-builder';
import {MsraId, SustainabilityRatingType, SustainabilityRatingTypeId, SustainabilityRatingTypeImpl} from "./ratings/sustainability-ratings.interface";
import {PagedModel} from "@app/shared/api/model/paged-model";
import {HttpClient} from "@angular/common/http";
import {CollectionUtils} from "@app/shared/util/collections/collection-utils";
import {ActivatedRouteSnapshot, ResolveFn} from "@angular/router";


export interface SustainabilityRatingTypeSearchTerm {
  sustainability_rating_type_id?: SustainabilityRatingTypeId;
  sustainability_rating_type_ids?: SustainabilityRatingTypeId[];
  msra_id?: MsraId;
  msra_ids?: MsraId[];
  name?: string;
  shared?: boolean;
  system?: boolean;
}

interface SustainabilityRatingTypeFullSearchTerm extends SustainabilityRatingTypeSearchTerm, PagingParams {}

interface SustainabilityRatingTypeCreateBody {
  max_score: number;
  min_score: number;
  msra_id: MsraId;
  name: string;
  shared: boolean;
  system: boolean;
}

const sustainabilityRatingTypeKeyword = 'sustainability_rating_type';

const sustainabilityRatingTypeVariables = {
  sustainability_rating_type_id: {type: GRAPHQL_TYPES.INTEGER},
  sustainability_rating_type_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
  msra_id: {type: GRAPHQL_TYPES.INTEGER},
  msra_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
  name: {type: GRAPHQL_TYPES.STRING},
  shared: {type: GRAPHQL_TYPES.BOOLEAN},
  system: {type: GRAPHQL_TYPES.BOOLEAN},
  ...GQLPagingParamsVariable
};


export const sustainabilityRatingTypeFields = [
  'sustainability_rating_type_id',
  'created_at',
  'customer_id',
  'last_modified_at',
  'msra_id',
  {main_sustainability_rating_attribute: ['msra_id', 'name_key']},
  'sustainability_rating_measure_ids',
  {sustainability_rating_measures: ['sustainability_rating_measure_id', 'name']},
  'max_score',
  'min_score',
  'name',
  'shared',
  'system'
];

export const sustainabilityRatingTypeResolver: ResolveFn<SustainabilityRatingType> = (route: ActivatedRouteSnapshot): Observable<SustainabilityRatingType> => {
  return inject(SustainabilityRatingTypeService).getSustainabilityRatingTypeById(+route.paramMap.get('id'));
};

@Injectable({providedIn: 'root'})
export class SustainabilityRatingTypeService extends BaseService {
  public static readonly modelMapping = {SustainabilityRatingType: SustainabilityRatingTypeImpl};

  private readonly DATA_KEY = sustainabilityRatingTypeKeyword;
  private readonly DATA_KEY_PLURAL = this.DATA_KEY + 's';
  private readonly URL_PART = '/sustainability-ratings/types';
  private readonly PAGE_SIZE = 2000;
  private readonly sustainabilityRatingTypeQuery = gql.query({
    operation: this.DATA_KEY_PLURAL,
    fields: sustainabilityRatingTypeFields,
    variables: sustainabilityRatingTypeVariables
  });

  constructor(
    private http: HttpClient,
    private httpUtilityService: HttpUtilityService,
    private classTransformer: ClassTransformer
  ) {
    super();
  }

  getSustainabilityRatingTypes(pageInfo: PagingParams, searchTerm: SustainabilityRatingTypeSearchTerm): Observable<PagedModel<SustainabilityRatingType[]>> {
    const fullSearchTerm: SustainabilityRatingTypeFullSearchTerm = {...searchTerm, ...pageInfo};
    return this.httpUtilityService.graphQL<SustainabilityRatingGraphqlModels.SustainabilityRatingType[]>(this.sustainabilityRatingTypeQuery, this.DATA_KEY_PLURAL, fullSearchTerm)
      .pipe(map(result => ({
        data: this.mapToInstance(result.data),
        count: result.count
      })));
  }

  getAllSustainabilityRatingTypes(): Observable<SustainabilityRatingType[]> {
    const getSustainabilityRatingTypes = (pageInfo: APIPageInfo) => this.getSustainabilityRatingTypes({start_index: pageInfo.first, limit: pageInfo.rows}, {});
    return this.httpUtilityService.pageUntilComplete(getSustainabilityRatingTypes, {first: 0, rows: this.PAGE_SIZE});
  }

  getSustainabilityRatingTypeById(id: number): Observable<SustainabilityRatingType> {
    return this.getSustainabilityRatingTypes(
      {limit: 1, start_index: 0},
      {sustainability_rating_type_id: id}).pipe(map(pagedRatingTypes => CollectionUtils.only(pagedRatingTypes.data))
    );
  }

  createSustainabilityRatingType(requestBody: SustainabilityRatingTypeCreateBody): Observable<SustainabilityRatingType> {
    const options = {headers: this.getHeaders()};
    const createRequestBody: {sustainability_rating_types: SustainabilityRatingTypeCreateBody[]} = {sustainability_rating_types: [requestBody]};
    return this.http.post(this.getGateway() + this.URL_PART, createRequestBody, options).pipe(
      map(response => this.httpUtilityService.extractResponseData<SustainabilityRatingGraphqlModels.SustainabilityRatingType>(response, this.DATA_KEY_PLURAL)),
      map(responseRatingType => this.mapToInstance(responseRatingType))
    );
  }

  updateSustainabilityRatingType(requestBody: SustainabilityRatingType): Observable<SustainabilityRatingType> {
    const options = {headers: this.getHeaders()};
    return this.http.put(this.getGateway() + this.URL_PART + '/' + requestBody.sustainability_rating_type_id, this.mapToPlain(requestBody), options).pipe(
      map(response => this.httpUtilityService.extractResponseData<SustainabilityRatingGraphqlModels.SustainabilityRatingType>(response, this.DATA_KEY)),
      map(responseRatingType => this.mapToInstance(responseRatingType))
    );
  }

  deleteSustainabilityRatingType(sustainabilityRatingType: SustainabilityRatingType): Observable<boolean> {
    const options = {headers: this.getHeaders()};
    return this.http.delete(this.getGateway() + this.URL_PART + '/' + sustainabilityRatingType.sustainability_rating_type_id, options)
      .pipe(map((response) => this.httpUtilityService.extractResponseData<{deleted: boolean}>(response, this.DATA_KEY).deleted));
  }

  private mapToInstance(ratingType: SustainabilityRatingGraphqlModels.SustainabilityRatingType[]): SustainabilityRatingType[];
  private mapToInstance(ratingType: SustainabilityRatingGraphqlModels.SustainabilityRatingType): SustainabilityRatingType;
  private mapToInstance(
    ratingType: SustainabilityRatingGraphqlModels.SustainabilityRatingType | SustainabilityRatingGraphqlModels.SustainabilityRatingType[]
  ): SustainabilityRatingType | SustainabilityRatingType[] {
    return this.classTransformer.plainToInstance(SustainabilityRatingTypeService.modelMapping.SustainabilityRatingType, ratingType);
  }

  private mapToPlain(ratingType: SustainabilityRatingType[]): Record<string, any>[];
  private mapToPlain(ratingType: SustainabilityRatingType): Record<string, any>;
  private mapToPlain(ratingType: SustainabilityRatingType | SustainabilityRatingType[]): Record<string, any> | Record<string, any>[] {
    return this.classTransformer.instanceToPlain(ratingType);
  }
}
